import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import cloneEmailCampaign from '../../../api/graphql/mutations/cloneEmailCampaign';
import Notification, { useNotification } from '../../Notification';

// Client-side mirror of the server's own check (cloneEmailCampaign.js,
// backend) - not the enforcement, just a clearer message before the round
// trip. The server still validates for real.
const KEY_PATTERN = /^[a-zA-Z0-9_]+$/;

// Clone modal for the email template editor (Task 9, phase 3). Rendered
// inside the shared Modal.jsx by EmailTemplateEditor.jsx, which owns the
// open/closed boolean (Products' toggle pattern - see ProductsContainer.jsx).
//
// Copies every active plate of `sourceScope` into a new campaign under
// `newKey`/`newTitle` (cloneEmailCampaign.js resolver -> util/email/cloneCampaign.js).
// The clone rules (spec §4) exist to stop one specific failure: cloning last
// year's presale and mailing this year's list with last year's dates.
//
// This copy used to promise "nothing sends until someone has been through all of
// them". That was never true and the wording is deliberately gone. needsReview is
// a badge with no gate behind it, cloneTemplate stamps every plate isActive:true,
// and the campaign's own isActive is not read by any send path at all. A clone is
// live the moment its scope word reaches a signup form. Say that, rather than
// offering a safety that does not exist.
const EmailCloneModal = ({ sourceScope, sourcePlates, sourcePlatesStatus, onClose }) => {
  const [newKey, setNewKey] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [keyError, setKeyError] = useState(null);
  const [isCloning, setIsCloning] = useState(false);
  const [cloneResult, setCloneResult] = useState(null);
  const [status, handleStatus] = useNotification();

  const handleClone = async () => {
    const trimmedKey = newKey.trim();
    const trimmedTitle = newTitle.trim();

    if (!trimmedKey || !trimmedTitle || isCloning) {
      return;
    }

    if (!KEY_PATTERN.test(trimmedKey)) {
      setKeyError('The key can only contain letters, numbers and underscores.');
      return;
    }
    setKeyError(null);

    setIsCloning(true);
    const result = await cloneEmailCampaign({
      sourceKey: sourceScope,
      newKey: trimmedKey,
      newTitle: trimmedTitle,
    });
    setIsCloning(false);

    if (result && result.isAuth && result.success && result.campaign) {
      setCloneResult(result);
    } else if (result && result.isAuth) {
      handleStatus('icon-close-solid', result.error || 'Clone failed.', 'red');
    } else if (result) {
      handleStatus('icon-close-solid', 'You are not authorized to clone campaigns.', 'red');
    } else {
      handleStatus('icon-close-solid', 'Clone failed — check your connection and try again.', 'red');
    }
  };

  if (cloneResult) {
    const [firstTemplate] = cloneResult.templates || [];
    return (
      <div className="emails-app emails-modal-card">
        <div className="mstack">
          <div className="banner good">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#2f7d5b" strokeWidth="1.8">
              <path d="M3 8.4l3.4 3.4L13 5.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>
              {'Cloned to '}
              <b>{cloneResult.campaign.key}</b>
              {'. Every plate is flagged needs review and still carries the source campaign’s '}
              {'copy — check dates and links now, because these plates are already live to any '}
              {'signup carrying the new scope word.'}
            </span>
          </div>
          <div className="mactions">
            <button type="button" className="btn ghost" onClick={onClose}>Close</button>
            {firstTemplate && (
              <Link
                to={`/cms/emails/${cloneResult.campaign.key}/${firstTemplate.language}`}
                className="btn"
                onClick={onClose}
              >
                View clone
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  const copiedPlates = (sourcePlates || []).filter(plate => plate.exists);

  return (
    <div className="emails-app emails-modal-card">
      <div className="mstack">
        <div className="banner">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#8a5a12" strokeWidth="1.7">
            <path d="M8 2l6.4 11.2H1.6L8 2z" strokeLinejoin="round" />
            <path d="M8 6.6v2.6M8 11h.01" strokeLinecap="round" />
          </svg>
          <span>
            Every plate is copied word for word from
            {' '}
            <b>{sourceScope}</b>
            {' — that is the point, and also the danger. Every copied plate is flagged '}
            {'needs review, but that is a label, not a lock: the clone can send from the '}
            {'moment its scope word reaches a signup form. Check dates, links and copy first.'}
          </span>
        </div>

        {status.text && <Notification {...status} />}

        <div className="field">
          <label htmlFor="email-clone-key">
            Scope word
            <input
              id="email-clone-key"
              type="text"
              className="textinput mono"
              value={newKey}
              onChange={(event) => {
                setNewKey(event.target.value);
                setKeyError(null);
              }}
              placeholder="scope_presale27"
              autoComplete="off"
            />
          </label>
          <p className="hint">
            This one word does both jobs: it is the flag set on the Braze profile, and it is
            what finds the email. Type it into the newsletter widget to start the campaign.
          </p>
          {keyError && <p className="hint keyerror">{keyError}</p>}
        </div>

        <div className="field">
          <label htmlFor="email-clone-title">
            Name
            <input
              id="email-clone-title"
              type="text"
              className="textinput"
              value={newTitle}
              onChange={event => setNewTitle(event.target.value)}
              placeholder="Barcelona 2027 · general presale"
              autoComplete="off"
            />
          </label>
          <p className="hint">Shown in this dashboard only. Subscribers never see it.</p>
        </div>

        {sourcePlatesStatus !== 'ready' && (
          <p className="hint">
            This campaign&apos;s plates could not be listed
            {sourcePlatesStatus === 'loading' ? ' yet' : ''}
            {' — the clone still copies every active plate, each one flagged needs review.'}
          </p>
        )}

        {copiedPlates.length > 0 && (
          <div className="field">
            <span className="field-label">{`Plates to copy · ${copiedPlates.length}`}</span>
            <div className="platerows">
              {copiedPlates.map(plate => (
                <div className="platerow" key={plate.language}>
                  <span className="plate-cell is-stale">{plate.language}</span>
                  <span className="subj">{plate.subject || <i>no subject</i>}</span>
                  <span className="state">needs review</span>
                </div>
              ))}
            </div>
            <p className="hint">
              Revision history is not copied — the new campaign starts with one revision, the
              clone itself.
            </p>
          </div>
        )}

        <div className="mactions">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn"
            onClick={handleClone}
            disabled={isCloning || !newKey.trim() || !newTitle.trim()}
          >
            {isCloning ? 'Cloning…' : 'Create campaign'}
          </button>
        </div>
      </div>
    </div>
  );
};

EmailCloneModal.propTypes = {
  sourceScope: PropTypes.string.isRequired,
  sourcePlates: PropTypes.arrayOf(PropTypes.shape({
    language: PropTypes.string,
    subject: PropTypes.string,
    exists: PropTypes.bool,
  })),
  // 'ready' | 'loading' | 'failed' - see buildPlateStrip in utils/emailPlates.js.
  // Anything but 'ready' means the list below is not the whole truth, and the
  // modal says so rather than showing a short list as if it were complete.
  sourcePlatesStatus: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

EmailCloneModal.defaultProps = {
  sourcePlates: [],
  sourcePlatesStatus: 'ready',
};

export default EmailCloneModal;
